import { useState } from 'react';
import styled from 'styled-components';
import {
  Flex,
  FlexCenter,
  FlexColumn,
  FlexSpace,
} from '@elementary/common-ui/components';
import DateRangePicker, {
  DateRangeValue,
} from '@elementary/common-ui/components/DateRangePicker';
import { CircularProgress, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { SourceType } from 'shared/types';
import { ExecutionHistoryItem } from 'shared/types/execution';
import PanelError from 'shared/components/PanelError';
import useTestOverviewExecutionHistory from './hooks/useTestOverviewExecutionHistory';
import ExecutionHistoryChart from './components/ExecutionHistoryChart';
import useDefaultDateRange from './hooks/useDefaultDateRange';

const Wrapper = styled(FlexColumn)`
  background-color: ${({ theme }) => theme.palette.common.gray15};
  padding: 16px 24px;
  gap: 24px;
  border-radius: 8px;
`;

export type DetailsComponentProps = {
  id: string;
  failures: number | null;
  description: string | null;
  onFeedbackClick?: () => void;
  isCloudTest?: boolean;
  sourceType: SourceType;
};

type Props = {
  DetailsComponent: ({
    id,
    failures,
    description,
    onFeedbackClick,
    isCloudTest,
  }: DetailsComponentProps) => JSX.Element;
  id: string;
  sourceType: SourceType;
  onFeedbackClick?: () => void;
  isCloudTest?: boolean;
  dateRange?: DateRangeValue;
};

const ExecutionHistory = ({
  DetailsComponent,
  id,
  sourceType,
  onFeedbackClick,
  isCloudTest,
  dateRange: dateRangeProp,
}: Props) => {
  const defaultDaysBack = useDefaultDateRange();

  const [selectedExecutionHistory, setSelectedExecutionHistory] =
    useState<ExecutionHistoryItem>();
  const [dateRange, setDateRange] = useState<DateRangeValue>(
    dateRangeProp ?? {
      start: dayjs().subtract(defaultDaysBack, 'days').startOf('day'),
      end: dayjs().endOf('hour'),
    },
  );

  const setFormattedDateRange = (range: DateRangeValue) => {
    setDateRange({
      start: range.start ? range.start.startOf('day') : null,
      end: range.end ? range.end.endOf('hour') : null,
    });
  };

  const { data, isFetching, error } = useTestOverviewExecutionHistory(
    dateRange,
    id,
    sourceType,
  );

  return (
    <Wrapper>
      <FlexSpace sx={{ alignItems: 'flex-end' }}>
        <FlexColumn>
          <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
            Execution History
          </Typography>
          <Typography sx={{ color: 'common.gray4' }}>
            Select an execution to view the result
          </Typography>
        </FlexColumn>

        <Flex>
          <DateRangePicker
            disableFuture
            value={dateRange}
            onChange={setFormattedDateRange}
          />
        </Flex>
      </FlexSpace>

      {isFetching || !data || error ? (
        <FlexCenter sx={{ height: '100%', padding: '0 24px' }}>
          {error ? (
            <PanelError
              error={error as Error}
              errorName="ApiError"
              page={'ExecutionHistory'}
            />
          ) : (
            <CircularProgress />
          )}
        </FlexCenter>
      ) : data?.executionHistory.length > 0 ? (
        <>
          <ExecutionHistoryChart
            executionHistory={data?.executionHistory}
            onExecutionHistorySelect={setSelectedExecutionHistory}
          />
          {selectedExecutionHistory && (
            <DetailsComponent
              id={selectedExecutionHistory.id}
              failures={selectedExecutionHistory.failures}
              description={selectedExecutionHistory.description}
              onFeedbackClick={onFeedbackClick}
              isCloudTest={isCloudTest}
              sourceType={sourceType}
            />
          )}
        </>
      ) : (
        <FlexCenter>
          <Typography variant="body1">
            No execution history available
          </Typography>
        </FlexCenter>
      )}
    </Wrapper>
  );
};

export default ExecutionHistory;
